import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Toolbar, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { PoApprovalService } from '../../services/poApproval.service';
import { AuthService } from '../../services/auth.service';
import { PoApprovalStatus } from '../../interfaces/procurement/poApproval.interface';
import { PurchaseOrder, BulkActionResult } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

type BulkResultView = {
  verb: string;
  result: BulkActionResult;
};

export const PendingApprovalPurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const canApprove = usePermission(PERMISSIONS.PURCHASE_ORDER_APPROVE); // approve and reject are PURCHASE_ORDER_APPROVE server-side
  const toast = useToast();
  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [resultView, setResultView] = useState<BulkResultView | null>(null);
  // B4-F: band and approval progress per PO (silent when the call is refused)
  const [approvalByPo, setApprovalByPo] = useState<Record<string, PoApprovalStatus>>({});

  const fetchPOs = async (page: number = 0) => {
    try {
      setLoading(true);
      const response = await PurchaseOrderService.listByInstitution(page, 10, 'PENDING_APPROVAL');
      setPos(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
      setSelected(new Set());
      const institutionId = AuthService.getSelectedInstitution();
      if (institutionId) {
        PoApprovalService.pending(institutionId)
          .then(rows => setApprovalByPo(Object.fromEntries((rows ?? []).map(r => [r.purchaseOrderId, r]))))
          .catch(() => setApprovalByPo({}));
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load pending approval purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPOs(page - 1);
  };

  const toggleRow = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = pos.length > 0 && pos.every(po => selected.has(po.id));
  const someOnPageSelected = pos.some(po => selected.has(po.id));

  const toggleAllOnPage = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pos.forEach(po => next.delete(po.id));
      } else {
        pos.forEach(po => next.add(po.id));
      }
      return next;
    });
  };

  const runBulk = async (
    verb: string,
    fn: (ids: string[]) => Promise<BulkActionResult>,
  ) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setSubmitting(true);
    try {
      const result = await fn(ids);
      setResultView({ verb, result });
      toast.showSuccess(`${result.succeeded.length} ${verb}, ${result.failed.length} failed`);
      await fetchPOs(pageInfo.currentPage);
    } catch (e) {
      toast.showApiError(e, `Failed to ${verb.replace(/d$/, '')} purchase orders`);
    } finally {
      setSubmitting(false);
    }
  };

  const poNumberFor = (id: string) => pos.find(p => p.id === id)?.poNumber ?? id;

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.pendingPo.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Pending Approval Purchase Orders</Typography>
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {selected.size > 0 && (
        <Toolbar disableGutters sx={{ gap: 2, mb: 1 }}>
          <Typography variant="body2">{selected.size} selected</Typography>
          {canApprove && (
            <Button
              data-help="procurement.pendingPo.bulkApprove"
              variant="contained"
              color="success"
              disabled={submitting}
              onClick={() => runBulk('approved', PurchaseOrderService.bulkApprove)}
            >
              Approve
            </Button>
          )}
          {canApprove && (
            <Button
              data-help="procurement.pendingPo.bulkReject"
              variant="outlined"
              color="error"
              disabled={submitting}
              onClick={() => runBulk('rejected', PurchaseOrderService.bulkReject)}
            >
              Reject
            </Button>
          )}
          <Button data-help="procurement.pendingPo.action.clear" variant="text" disabled={submitting} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Toolbar>
      )}

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper} data-help-section="procurement.pendingPo.table">
          <Table>
            <TableHead>
              <TableRow>
                {canApprove && (
                  <TableCell padding="checkbox" data-help="procurement.pendingPo.col.select">
                    <Checkbox
                      data-help="procurement.pendingPo.action.selectAll"
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      onChange={toggleAllOnPage}
                      inputProps={{ 'aria-label': 'select all on page' }}
                    />
                  </TableCell>
                )}
                <TableCell data-help="procurement.pendingPo.col.number">PO Number</TableCell>
                <TableCell data-help="procurement.pendingPo.col.supplier">Supplier</TableCell>
                <TableCell data-help="procurement.pendingPo.col.date">Order Date</TableCell>
                <TableCell data-help="procurement.pendingPo.col.status">Status</TableCell>
                <TableCell data-help="procurement.pendingPo.col.total">Total</TableCell>
                <TableCell data-help="procurement.pendingPo.col.band">Band</TableCell>
                <TableCell data-help="procurement.pendingPo.col.approvals">Approvals</TableCell>
                <TableCell data-help="procurement.pendingPo.col.waiting">Waiting for</TableCell>
                <TableCell data-help="procurement.pendingPo.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pos.map(po => (
                <TableRow key={po.id} selected={selected.has(po.id)}>
                  {canApprove && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        data-help="procurement.pendingPo.action.select"
                        checked={selected.has(po.id)}
                        onChange={() => toggleRow(po.id)}
                        inputProps={{ 'aria-label': `select ${po.poNumber}` }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{po.poNumber}</TableCell>
                  <TableCell title={po.supplierId}>{po.supplierName ?? po.supplierId}</TableCell>
                  <TableCell>{formatDate(po.orderDate)}</TableCell>
                  <TableCell>
                    <Chip data-help="procurement.pendingPo.status" label={humanizeEnum(po.status)} color="warning" size="small" />
                  </TableCell>
                  <TableCell>{formatMoney(po.totalAmount)}</TableCell>
                  <TableCell>{approvalByPo[po.id]?.bandLabel ?? '-'}</TableCell>
                  <TableCell>
                    {approvalByPo[po.id] ? `${approvalByPo[po.id].approvalsGiven} of ${approvalByPo[po.id].approvalsRequired}` : '-'}
                  </TableCell>
                  <TableCell>{approvalByPo[po.id]?.remaining ?? '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        data-help="procurement.pendingPo.action.view"
                        size="small"
                        color="primary"
                        data-testid={`view-po-${po.id}`}
                        onClick={() => navigate(`/procurement/purchase-orders/pending-approval/${po.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog data-help-page="procurement.pendingPo.resultDialog" open={resultView !== null} onClose={() => setResultView(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resultView
            ? `${resultView.result.succeeded.length} ${resultView.verb}, ${resultView.result.failed.length} failed`
            : ''}
        </DialogTitle>
        <DialogContent>
          {resultView && resultView.result.failed.length > 0 ? (
            <Stack spacing={1}>
              <DialogContentText>Some purchase orders could not be processed:</DialogContentText>
              {resultView.result.failed.map(f => (
                <Typography key={f.id} variant="body2">
                  {poNumberFor(f.id)} — {f.error}
                </Typography>
              ))}
            </Stack>
          ) : (
            <DialogContentText>All selected purchase orders were processed.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.pendingPo.resultClose" onClick={() => setResultView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingApprovalPurchaseOrders;
